import {ChangeDetectionStrategy, Component, computed, inject, input, signal} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {RfdAppointmentStatus, RfdPlace} from '@frontend/shared-api';
import {ColorLegendComponent} from '../../../../../../../../shared/components/color-legend/color-legend.component';
import {ColorLegendEntry} from '../../../../../../../../shared/components/color-legend/color-legend.types';
import {RfdCalendarAppointment, RfdCalendarDay} from './rfd-schedule-calendar.types';

const DAYS_PER_WEEK = 7;
const KEY = 'project.steps.rfd.invitationList.';

export const APPOINTMENT_STATUS_COLORS: Record<RfdAppointmentStatus | 'NONE', string> = {
  [RfdAppointmentStatus.CONFIRMED]: 'var(--color-green)',
  [RfdAppointmentStatus.PLANNED]: 'var(--color-blue)',
  NONE: 'var(--p-surface-400)',
};

@Component({
  selector: 'app-rfd-schedule-calendar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, Button, Tooltip, ColorLegendComponent],
  templateUrl: './rfd-schedule-calendar.component.html',
})
export class RfdScheduleCalendarComponent {
  private readonly transloco = inject(TranslocoService);

  readonly appointments = input.required<RfdCalendarAppointment[]>();
  readonly today = input<Date>(new Date());

  private readonly selectedMonth = signal<Date | null>(null);

  protected readonly legendEntries: ColorLegendEntry[] = [
    {
      color: APPOINTMENT_STATUS_COLORS[RfdAppointmentStatus.PLANNED],
      labelKey: `${KEY}appointmentStatus.planned`,
    },
    {
      color: APPOINTMENT_STATUS_COLORS[RfdAppointmentStatus.CONFIRMED],
      labelKey: `${KEY}appointmentStatus.confirmed`,
    },
    {color: APPOINTMENT_STATUS_COLORS.NONE, labelKey: `${KEY}calendar.noStatus`},
  ];

  protected readonly visibleMonth = computed(
    () => this.selectedMonth() ?? this.determineInitialMonth(),
  );

  protected readonly monthTitle = computed(() =>
    new Intl.DateTimeFormat(this.transloco.getActiveLang(), {
      month: 'long',
      year: 'numeric',
    }).format(this.visibleMonth()),
  );

  protected readonly weekdayLabels = computed(() => {
    const formatter = new Intl.DateTimeFormat(this.transloco.getActiveLang(), {weekday: 'short'});
    const firstMonday = new Date(2024, 0, 1);
    return Array.from({length: DAYS_PER_WEEK}, (_, offset) =>
      formatter.format(this.addDays(firstMonday, offset)),
    );
  });

  protected readonly weeks = computed((): RfdCalendarDay[][] => {
    const month = this.visibleMonth();
    const gridStart = this.startOfWeek(month);
    const lastOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const weekCount = Math.ceil((this.daysBetween(gridStart, lastOfMonth) + 1) / DAYS_PER_WEEK);
    const appointmentsByDay = this.groupAppointmentsByDay();

    return Array.from({length: weekCount}, (_, week) =>
      Array.from({length: DAYS_PER_WEEK}, (_, weekday) => {
        const date = this.addDays(gridStart, week * DAYS_PER_WEEK + weekday);
        return {
          date,
          inVisibleMonth: date.getMonth() === month.getMonth(),
          isToday: this.isSameDay(date, this.today()),
          appointments: appointmentsByDay.get(this.dayKey(date)) ?? [],
        };
      }),
    );
  });

  protected readonly hasAppointments = computed(() => this.appointments().length > 0);

  protected showPreviousMonth(): void {
    this.shiftVisibleMonth(-1);
  }

  protected showNextMonth(): void {
    this.shiftVisibleMonth(1);
  }

  protected showCurrentMonth(): void {
    this.selectedMonth.set(this.startOfMonth(this.today()));
  }

  protected formatTime(date: Date): string {
    return new Intl.DateTimeFormat(this.transloco.getActiveLang(), {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  protected resolveStatusColor(appointment: RfdCalendarAppointment): string {
    return APPOINTMENT_STATUS_COLORS[appointment.appointmentStatus ?? 'NONE'];
  }

  protected resolvePlaceIcon(place: RfdPlace | null): string | null {
    if (place === RfdPlace.REMOTE) {
      return 'pi pi-video';
    }
    return place === RfdPlace.ON_SITE ? 'pi pi-map-marker' : null;
  }

  protected resolvePlaceLabelKey(place: RfdPlace | null): string {
    return place === RfdPlace.REMOTE ? `${KEY}place.remote` : `${KEY}place.onSite`;
  }

  private determineInitialMonth(): Date {
    const sortedStarts = this.appointments()
      .map((appointment) => appointment.start)
      .sort((first, second) => first.getTime() - second.getTime());
    const startOfToday = this.startOfDay(this.today());
    const nextAppointment = sortedStarts.find((start) => start >= startOfToday);
    return this.startOfMonth(nextAppointment ?? sortedStarts.at(-1) ?? this.today());
  }

  private groupAppointmentsByDay(): Map<string, RfdCalendarAppointment[]> {
    return [...this.appointments()]
      .sort((first, second) => first.start.getTime() - second.start.getTime())
      .reduce((byDay, appointment) => {
        const key = this.dayKey(appointment.start);
        byDay.set(key, [...(byDay.get(key) ?? []), appointment]);
        return byDay;
      }, new Map<string, RfdCalendarAppointment[]>());
  }

  private shiftVisibleMonth(months: number): void {
    const month = this.visibleMonth();
    this.selectedMonth.set(new Date(month.getFullYear(), month.getMonth() + months, 1));
  }

  private startOfWeek(date: Date): Date {
    const daysSinceMonday = (date.getDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
    return this.addDays(this.startOfDay(date), -daysSinceMonday);
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  }

  private daysBetween(start: Date, end: Date): number {
    return Math.round(
      (this.startOfDay(end).getTime() - this.startOfDay(start).getTime()) / 86_400_000,
    );
  }

  private isSameDay(first: Date, second: Date): boolean {
    return this.dayKey(first) === this.dayKey(second);
  }

  private dayKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }
}
